// shared utilites for tables
import { rankItem } from "@tanstack/match-sorter-utils"
import { Row } from "@tanstack/react-table"

/** Split pasted list input (comma/newline/tab/semicolon and Unicode line breaks Excel/tools may emit). Single segment keeps full trimmed string so spaced phrases stay one token. */
export function split_filter_list_input(raw: string | number | null | undefined): string[] {
  if (raw == null) return []
  const trimmed = String(raw).trim()
  if (!trimmed) return []
  const segments = trimmed
    .split(/[,;\n\r\t\u2028\u2029]+/)
    .map(s => s.trim())
    .filter(Boolean)
  if (segments.length <= 1) {
    return segments.length === 1 ? [segments[0]!] : []
  }
  return segments
}

export function normalizeValue(value: any, columnId: string): string {
  if (!value) return ""
  const stringValue = value.toString()

  if (columnId.includes("date")) {
    return stringValue.replace(/-/g, "")
  }
  if (columnId.includes("amount")) {
    const numericValue = parseFloat(stringValue.replace(/[$,]/g, ""))
    return numericValue.toString()
  }
  return stringValue
}

function date_filter_single_token(row: any, columnId: string, token: string): boolean {
  const cellValue = row.getValue(columnId)
  if (!cellValue) return false

  let normalizedFilterValue = token.toString()
  if (normalizedFilterValue.match(/^\d{1,2}[-/]\d{1,2}([-/]\d{1,4})?$/)) {
    const [month, day, year] = normalizedFilterValue.split(/[-/]/)
    normalizedFilterValue = `${year || ""}${month.padStart(2, "0")}${day.padStart(2, "0")}`
  } else {
    normalizedFilterValue = normalizeValue(token, columnId)
  }

  const normalizedCellValue = normalizeValue(cellValue, columnId)
  return normalizedCellValue.includes(normalizedFilterValue)
}

export function dateFilter(row: any, columnId: string, value: string) {
  const tokens = split_filter_list_input(value)
  if (tokens.length === 0) return true
  return tokens.some(token => date_filter_single_token(row, columnId, token))
}

let printed: any = {}

function fuzzy_filter_single_token<T>(row: Row<T>, columnId: string, query: string, addMeta: any): boolean {
  const ACCOUNTING_SEARCH_COLUMNS = ["reason_code", "expense_account"]
  if (!(columnId in printed)) {
    printed[columnId] = true
  }
  if (query.toLowerCase() === "uncoded" && ACCOUNTING_SEARCH_COLUMNS.includes(columnId)) {
    if (row.getValue(columnId) === "") {
      return true
    }
  }

  let value = row.getValue(columnId) as any
  if (!value) {
    return false
  }
  let queryPart = query
  if (columnId === "invoice_number") {
  } else if (columnId.includes("amount")) {
    if (/^[\$,.\d]+$/.test(query.toString())) {
      value = parseFloat(value.toString().replace(/[\$,]/g, "")).toString()
    } else {
      return false
    }
  } else if (columnId.includes("date")) {
    // why are we doing this for dates? that is bizarre
    queryPart = query.replace(/-/g, "")
    value = value.toString().replace(/-/g, "")
  }

  const itemRank = rankItem(value, queryPart)
  addMeta({ itemRank })
  return itemRank.passed
}

export function fuzzyFilter<T>(row: Row<T>, columnId: string, query: string, addMeta: any) {
  const tokens = split_filter_list_input(query ?? "")
  if (tokens.length === 0) return true
  return tokens.some(token => fuzzy_filter_single_token(row, columnId, token, addMeta))
}

function _defaultFilter(row: any, columnId: string, query: any): boolean {
  const tokens = split_filter_list_input(query)
  if (tokens.length === 0) return true
  const value = row.getValue(columnId)
  if (value == null || value === "") {
    return false
  }
  const valueStr = String(value).toLowerCase()
  return tokens.some(token => valueStr.includes(token.toLowerCase()))
}
// Cast to any to allow use with any table type
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const defaultFilter: any = _defaultFilter

// Test for fuzzyFilter function
if (import.meta.vitest) {
  describe("split_filter_list_input", () => {
    it("returns one token for phrase with spaces only", () => {
      expect(split_filter_list_input("clearvue reporting")).toEqual(["clearvue reporting"])
    })

    it("splits newline-separated pasted list", () => {
      expect(split_filter_list_input("222247\n223532\nE548245")).toEqual(["222247", "223532", "E548245"])
    })

    it("splits comma-separated values", () => {
      expect(split_filter_list_input("222247,223532,E548245")).toEqual(["222247", "223532", "E548245"])
    })

    it("returns empty for whitespace-only input", () => {
      expect(split_filter_list_input("   ")).toEqual([])
    })

    it("trims tokens and drops empties from stray delimiters", () => {
      expect(split_filter_list_input("  hello  ,\n  world\t")).toEqual(["hello", "world"])
    })

    it("splits on Unicode line/paragraph separators", () => {
      expect(split_filter_list_input(`a\u2028b\u2029c`)).toEqual(["a", "b", "c"])
    })
  })

  describe("fuzzyFilter", () => {
    const mockAddMeta = vi.fn()

    it("should handle deduction_amount column with specific value", () => {
      const row = {
        getValue: vi.fn().mockReturnValue("$1,234.56"),
      } as any as Row<void>

      const result = fuzzyFilter(row, "deduction_amount", "01OVP0525048556NYGR", mockAddMeta)

      expect(result).toBe(false)
    })

    it('should return false for "new" when status_value is not empty', () => {
      const row = {
        getValue: vi.fn(() => "active"),
      }
      // @ts-ignore
      expect(fuzzyFilter(row, "status_value", "new", mockAddMeta)).toBe(false)
    })

    it('should return true for "uncoded" when reason_code or expense_account is empty', () => {
      const row = {
        getValue: vi.fn(key => (key === "reason_code" || key === "expense_account" ? "" : "value")),
      }
      // @ts-ignore
      expect(fuzzyFilter(row, "reason_code", "uncoded", mockAddMeta)).toBe(true)
      // @ts-ignore
      expect(fuzzyFilter(row, "expense_account", "uncoded", mockAddMeta)).toBe(true)
    })

    it('should return false for "uncoded" when reason_code and expense_account are not empty', () => {
      const row = {
        getValue: vi.fn(() => "some value"),
      }
      // @ts-ignore
      expect(fuzzyFilter(row, "reason_code", "uncoded", mockAddMeta)).toBe(false)
      // @ts-ignore
      expect(fuzzyFilter(row, "expense_account", "uncoded", mockAddMeta)).toBe(false)
    })

    it("should use rankItem for fuzzy matching when not handling special cases", () => {
      const row = {
        getValue: vi.fn(() => "test value"),
      } as any as Row<void>
      expect(fuzzyFilter(row, "some_column", "test", mockAddMeta)).toBe(true)
      expect(fuzzyFilter(row, "some_column", "value", mockAddMeta)).toBe(true)
      expect(fuzzyFilter(row, "some_column", "other", mockAddMeta)).toBe(false)
    })

    it("should return false when item value is falsy", () => {
      const row = {
        getValue: vi.fn(() => ""),
      }
      // @ts-ignore
      expect(fuzzyFilter(row, "some_column", "test", mockAddMeta)).toBe(false)
    })

    it("should handle invoice_number column with specific value", () => {
      let row = {
        getValue: vi.fn().mockReturnValue("0393181"),
      } as any as Row<void>
      let result = fuzzyFilter(row, "invoice_number", "0393181", mockAddMeta)
      expect(result).toBe(true)
      row = {
        getValue: vi.fn().mockReturnValue("74257968"),
      } as any as Row<void>
      result = fuzzyFilter(row, "invoice_number", "74257968", mockAddMeta)
      expect(result).toBe(true)
    })
    it("should filter category description", () => {
      let row = {
        getValue: vi.fn().mockReturnValue("ClearVue Reporting"),
      } as any as Row<void>
      let result = fuzzyFilter(row, "category_description", "clearvue", mockAddMeta)
      expect(result).toBe(true)
    })

    it("matches if any list token matches (OR)", () => {
      const row = {
        getValue: vi.fn(() => "74257968"),
      } as any as Row<void>
      expect(fuzzyFilter(row, "invoice_number", "0393181\n74257968", mockAddMeta)).toBe(true)
      expect(fuzzyFilter(row, "invoice_number", "0393181,999999", mockAddMeta)).toBe(false)
    })
  })

  describe("defaultFilter", () => {
    it("matches if any token is contained in cell", () => {
      const row = { getValue: () => "E548245" }
      expect(defaultFilter(row, "invoice_number", "222247\nE548245")).toBe(true)
      expect(defaultFilter(row, "invoice_number", "222247,999999")).toBe(false)
    })

    it("null or undefined filter value matches all rows", () => {
      const row = { getValue: () => "anything" }
      expect(defaultFilter(row, "invoice_number", undefined)).toBe(true)
      expect(defaultFilter(row, "invoice_number", null)).toBe(true)
    })
  })

  describe("dateFilter", () => {
    // Helper to create a mock row with a date value
    const createRow = (date: string) => ({
      getValue: () => date,
    })

    test("matches MM/DD format against ISO dates", () => {
      expect(dateFilter(createRow("2024-06-27"), "invoice_date", "6/27")).toBe(true)
      expect(dateFilter(createRow("2024-06-27"), "invoice_date", "6/28")).toBe(false)
      expect(dateFilter(createRow("2024-07-27"), "invoice_date", "6/27")).toBe(false)
    })

    test("handles single digit month/day with or without leading zeros", () => {
      expect(dateFilter(createRow("2024-06-07"), "invoice_date", "6/7")).toBe(true)
      expect(dateFilter(createRow("2024-06-07"), "invoice_date", "06/07")).toBe(true)
      expect(dateFilter(createRow("2024-06-07"), "invoice_date", "6/07")).toBe(true)
    })

    test("matches full ISO date format exactly", () => {
      expect(dateFilter(createRow("2024-06-27"), "check_date", "2024-06-27")).toBe(true)
      expect(dateFilter(createRow("2024-06-27"), "check_date", "2023-06-27")).toBe(false)
    })

    test("handles empty and invalid values", () => {
      expect(dateFilter(createRow(""), "date", "6/27")).toBe(false)
      expect(dateFilter(createRow("invalid"), "date", "6/27")).toBe(false)
      expect(dateFilter(createRow("2024-06-27"), "date", "invalid")).toBe(false)
    })

    test("handles different date separators", () => {
      expect(dateFilter(createRow("2024-06-27"), "date", "6-27")).toBe(true)
      expect(dateFilter(createRow("2024-06-27"), "date", "6/27")).toBe(true)
    })

    test("matches if any date token matches", () => {
      expect(dateFilter(createRow("2024-06-27"), "invoice_date", "6/28\n6/27")).toBe(true)
      expect(dateFilter(createRow("2024-06-27"), "invoice_date", "6/28,7/01")).toBe(false)
    })
  })
}
